import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';
import {
  buildCountryTitleListParams,
  normalizeCountryTitleStatus,
  toCountryTitleApiPayload,
} from '../../../utils/country-title-catalog.utils';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Tipos de cargo por país.
 * Endpoint: `/settings/countries/{countryId}/type-charges`
 */
@Injectable({ providedIn: 'root' })
export class PositionTypeService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private resource(countryId: string, id?: string | number): string {
    const baseUrl = `${this.apiUrl}/${countryId}/type-charges`;
    return id !== undefined && id !== null ? `${baseUrl}/${id}` : baseUrl;
  }

  private mapItem(it: any): PositionType {
    return {
      id: it.id,
      name: it.name || it.title || '',
      detail: it.detail ?? false,
      status: normalizeCountryTitleStatus(it.status),
    };
  }

  getPositionTypes(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<PositionTypePageResponse> {
    const params = buildCountryTitleListParams({
      page,
      size,
      sort,
      direction,
      search,
      status,
    });
    return this.httpClient
      .get<ApiResponse<PageableData>>(this.resource(countryId), { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((it: any) => this.mapItem(it)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          } as PositionTypePageResponse;
        }),
      );
  }

  getPositionTypeById(countryId: string, id: string | number) {
    return this.httpClient
      .get<ApiResponse<any>>(this.resource(countryId, id))
      .pipe(map((resp) => this.mapItem(resp.data)));
  }

  createPositionType(countryId: string, payload: any) {
    return this.httpClient
      .post<{ success: boolean; message: string }>(
        this.resource(countryId),
        toCountryTitleApiPayload(payload),
      )
      .pipe(map((res) => ({ message: res.message || 'Tipo de cargo creado' })));
  }

  updatePositionType(countryId: string, id: string | number, payload: any) {
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        this.resource(countryId, id),
        toCountryTitleApiPayload(payload),
      )
      .pipe(map((res) => ({ message: res.message || 'Tipo de cargo actualizado' })));
  }

  deletePositionType(countryId: string, id: string | number) {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(
        this.resource(countryId, id),
      )
      .pipe(map((res) => ({ message: res.message || 'Tipo de cargo eliminado' })));
  }
}

export interface PositionType {
  id: string;
  name: string;
  status?: string;
  detail?: boolean;
}

export interface PositionTypePageResponse {
  items: PositionType[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

